import { useState, useMemo, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import { Flame } from "lucide-react";
import type { DbLead } from "@/lib/types";

const dailyVolumeChartConfig = { count: { label: "Leads", color: "hsl(var(--gold))" } };

const RANGES = [
  { value: "7days", label: "Last 7 Days", days: 7 },
  { value: "30days", label: "Last 30 Days", days: 30 },
  { value: "90days", label: "Last 90 Days", days: 90 },
  { value: "year", label: "This Year", days: 365 },
];

// Safety cap on the range query — well above any real agency's lead volume
// in a year, just to bound worst-case payload size.
const FETCH_CAP = 5000;

export default function OwnerAnalytics() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const [range, setRange] = useState("30days");
  const [inRange, setInRange] = useState<DbLead[]>([]);
  const seg = (profile as any)?.segment ?? "homecare";

  const days = RANGES.find(r => r.value === range)?.days ?? 30;

  // Query the selected window server-side instead of filtering a client-side
  // cache capped at the 500 most-recent leads — that cap meant "Last 30/90
  // Days" and "This Year" silently showed the exact same (truncated) data as
  // "Last 7 Days" for any account with 500+ recently-created leads.
  useEffect(() => {
    if (!user) { setInRange([]); return; }
    let cancelled = false;
    const cutoffIso = new Date(Date.now() - days * 86_400_000).toISOString();
    (async () => {
      const { data } = await supabase
        .from("leads")
        .select("*")
        .gte("created_at", cutoffIso)
        .order("created_at", { ascending: false })
        .limit(FETCH_CAP);
      if (!cancelled) setInRange((data as DbLead[]) ?? []);
    })();
    return () => { cancelled = true; };
  }, [user, days]);

  const total = inRange.length;
  const hot = inRange.filter(l => l.score >= 85).length;
  const avgScore = total > 0 ? Math.round(inRange.reduce((s, l) => s + l.score, 0) / total) : 0;
  const closed = inRange.filter(l => l.status === "closed").length;
  const conversion = total > 0 ? Math.round((closed / total) * 100) : 0;
  const pipeline = inRange.filter(l => l.status !== "lost").reduce((s, l) => s + (l.hours_needed ?? 0) * 25 * 4.33, 0);

  // Build daily lead volume for chart
  const dailyData = useMemo(() => {
    const buckets: Record<string, number> = {};
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86_400_000);
      const key = d.toISOString().slice(0, 10);
      buckets[key] = 0;
    }
    inRange.forEach(l => {
      const key = new Date(l.created_at).toISOString().slice(0, 10);
      if (key in buckets) buckets[key]++;
    });
    return Object.entries(buckets).map(([date, count]) => ({ date, count }));
  }, [inRange, days]);

  const payerSources = seg === "grouphome"
    ? ["DD Waiver", "MH Waiver", "CCC+ Waiver", "Private Pay"]
    : ["Private Pay", "Long-Term Care Insurance", "Medicaid Waiver", "Veterans Benefits"];

  const payerCounts = payerSources.map(src => ({
    label: src,
    count: inRange.filter(l => (l.payer_source ?? "").toLowerCase().includes(src.split(" ")[0].toLowerCase())).length,
  }));
  const maxPayer = Math.max(1, ...payerCounts.map(p => p.count));

  // Outcomes
  const outcomes = {
    won: inRange.filter(l => l.status === "closed").length,
    inProgress: inRange.filter(l => l.status === "contacted" || l.status === "follow_up" || l.status === "voicemail" || l.status === "booked").length,
    lost: inRange.filter(l => l.status === "lost").length,
    cold: inRange.filter(l => l.status === "new").length,
  };
  const outcomeTotal = Math.max(1, outcomes.won + outcomes.inProgress + outcomes.lost + outcomes.cold);

  const tiles = [
    { label: "Total Leads Captured", value: total },
    { label: "Hot Lead Rate", value: total > 0 ? `${Math.round((hot / total) * 100)}%` : "0%" },
    { label: "Average Lead Score", value: avgScore },
    { label: "Estimated Pipeline Value", value: `$${Math.round(pipeline).toLocaleString()}` },
    { label: "Leads Converted to Clients", value: closed },
    { label: "Conversion Rate", value: `${conversion}%` },
  ];

  return (
    <div className="space-y-6">
      {/* Header with range */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-navy">Analytics</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Pipeline performance and team insights.</p>
        </div>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RANGES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Section 1 — Pipeline & Revenue */}
      <section>
        <h2 className="text-lg font-bold text-navy mb-3">Pipeline & Revenue</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {tiles.map(t => (
            <div key={t.label} className="bg-card rounded-xl border p-4 shadow-sm transition-shadow duration-200 hover:shadow-md">
              <div className="text-2xl font-bold text-navy">{t.value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t.label}</div>
            </div>
          ))}
        </div>

        {/* Lead volume chart */}
        <div className="bg-card rounded-xl border p-5 shadow-sm mt-4">
          <h3 className="font-bold text-navy text-sm mb-3">Lead Capture Volume</h3>
          {dailyData.length > 1 ? (
            <ChartContainer config={dailyVolumeChartConfig} className="h-40 w-full">
              <LineChart data={dailyData} margin={{ left: 0, right: 10, top: 5, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={30} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={24} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Line type="monotone" dataKey="count" stroke="hsl(var(--gold))" strokeWidth={2} dot={{ r: 2.5 }} activeDot={{ r: 4 }} />
              </LineChart>
            </ChartContainer>
          ) : (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">Not enough data yet</div>
          )}
        </div>

        {/* Payer breakdown */}
        <div className="bg-card rounded-xl border p-5 shadow-sm mt-4">
          <h3 className="font-bold text-navy text-sm mb-3">Leads by Payer Source</h3>
          <div className="space-y-2">
            {payerCounts.map(p => (
              <div key={p.label} className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-44 shrink-0">{p.label}</span>
                <div className="flex-1 h-6 bg-muted rounded-r-lg overflow-hidden">
                  <div className="h-full bg-gold rounded-r-lg transition-all duration-500" style={{ width: `${(p.count / maxPayer) * 100}%` }} />
                </div>
                <span className="text-xs font-bold text-navy w-12 text-right">{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4 — Outcomes (donut + stats) */}
      <section>
        <h2 className="text-lg font-bold text-navy mb-3">Lead Outcomes Analysis</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-card rounded-xl border p-5 shadow-sm">
            <h3 className="font-bold text-navy text-sm mb-3">Outcome Breakdown</h3>
            <div className="flex items-center gap-5">
              <Donut
                segments={[
                  { label: "Won", value: outcomes.won, color: "hsl(var(--success))" },
                  { label: "In Progress", value: outcomes.inProgress, color: "hsl(var(--primary))" },
                  { label: "Lost", value: outcomes.lost, color: "hsl(var(--hot))" },
                  { label: "Cold", value: outcomes.cold, color: "hsl(var(--muted-foreground))" },
                ]}
                total={outcomeTotal}
              />
              <ul className="space-y-1.5 text-xs">
                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-success" /> Closed Won <span className="font-bold ml-1">{outcomes.won}</span></li>
                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-primary" /> In Progress <span className="font-bold ml-1">{outcomes.inProgress}</span></li>
                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-hot" /> Lost <span className="font-bold ml-1">{outcomes.lost}</span></li>
                <li className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-muted-foreground" /> Cold <span className="font-bold ml-1">{outcomes.cold}</span></li>
              </ul>
            </div>
          </div>

          <div className="bg-card rounded-xl border p-5 shadow-sm">
            <h3 className="font-bold text-navy text-sm mb-3">Time to Close</h3>
            <div className="space-y-3">
              <Tile label="Avg Days Lead → Close" value="8.3" />
              <Tile label="Fastest Close" value="2 days" />
              <Tile label="Slowest Close" value="34 days" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3 bg-muted/20">
      <div className="text-xl font-bold text-navy">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function Donut({ segments, total }: { segments: { label: string; value: number; color: string }[]; total: number }) {
  const r = 40;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="relative shrink-0">
      <svg width="120" height="120" viewBox="0 0 100 100">
        {segments.map((s, idx) => {
          const pct = total > 0 ? s.value / total : 0;
          if (pct === 0) return null;
          const dash = pct * circ;
          const thisOffset = offset;
          offset += dash;
          return (
            <circle
              key={idx}
              cx="50" cy="50" r={r}
              fill="none" stroke={s.color} strokeWidth="14"
              strokeDasharray={`${dash} ${circ - dash}`}
              strokeDashoffset={-thisOffset}
              transform="rotate(-90 50 50)"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold text-navy">{total}</span>
        <span className="text-[10px] text-muted-foreground">Total</span>
      </div>
    </div>
  );
}
